import type { PaginatedResponse, PaginationParams } from './types';

/** Maximum page size enforced by the backend validation pipe. */
export const MAX_API_LIMIT = 100;

const DEFAULT_LIMIT = 20;

/**
 * Clamp a requested page size to the API maximum (100).
 * Values below 1 fall back to the backend default (20).
 */
export function clampLimit(limit?: number): number {
  if (limit == null || limit < 1) return DEFAULT_LIMIT;
  return Math.min(limit, MAX_API_LIMIT);
}

/**
 * Normalize pagination params before sending to the API.
 */
export function normalizePaginationParams(
  params?: PaginationParams,
): { page: number; limit: number } {
  return {
    page: params?.page != null && params.page >= 1 ? params.page : 1,
    limit: clampLimit(params?.limit),
  };
}

/**
 * Fetch every page for a paginated endpoint (limit capped at {@link MAX_API_LIMIT}).
 */
export async function fetchAllPages<T>(
  fetchPage: (page: number, limit: number) => Promise<PaginatedResponse<T>>,
  limit = MAX_API_LIMIT,
): Promise<T[]> {
  const pageSize = clampLimit(limit);
  let page = 1;
  const all: T[] = [];

  while (true) {
    const res = await fetchPage(page, pageSize);
    all.push(...res.items);

    const totalPages = Math.max(1, Math.ceil(res.total / res.limit));
    if (page >= totalPages || res.items.length === 0) break;
    page += 1;
  }

  return all;
}
